/* eslint-disable @typescript-eslint/no-unused-vars */
import { faMicrophone, faPaperPlane, faPause, faRedo, faUser } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ChatUserInput from '../../components/ChatUserInput/ChatUserInput'
import Tooltip from '../../components/Tooltip/Tooltip'
import { groupListByDate, usePeperoniAiChat } from '../../utility/hooks'
import ChatList, { ChatListElement } from '../ChatList/ChatList'
import './ChatPanel.sass'
import { useAppSelector } from '../../store/reducers/store'
import { ContentType, Hint, IntegrationType, MessageType, ParsedMessage, Topic } from '../../types/data'
import Typewriter from '../../components/ChatMessage/Typewriter'
import { getHints } from '../../utility/api'
import HintsSection from './HintsSection'
import { setSelectedHint } from '../../store/actions/data'
import TopicSection from './TopicSection'
import AlertBox from '../../components/AlertBox/AlertBox'
import { useTranslation } from 'react-i18next'
import { useHeygenRealtime } from '../../services/heygen/heygen'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'
import CircularLoader from '../InfiniteLoader/CircularLoader'
import { useSearchParams } from 'react-router-dom'

const ChatPanel: React.FC = () => {
    const { t } = useTranslation()
    const listRef = useRef<ChatListElement | null>(null)
    const data = useAppSelector((data) => data.data)
    const [headerMessage, setHeaderMessage] = useState<string>('')
    const [selectedTopic, setSelectedTopic] = useState<Topic | undefined>()
    const [selectedHint] = useState<Hint | undefined>()
    const [hints, setHints] = useState<Hint[]>([])
    const [alertMessage, setAlertMessage] = useState<{ title: string; body: string } | undefined>()
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const [messageText, setMessageText] = useState<string>('')
    const [isHeygenVisible, setIsHeygenVisible] = useState<boolean>(false)
    const { listening, resetTranscript, finalTranscript } = useSpeechRecognition()
    const [queryParams] = useSearchParams()

    const getHeygenIntegration = useMemo(() => {
        const defaultHeygenIntegration = { avatar_id: '', token: '' }
        if (!data.integrations || data.integrations.length === 0) return defaultHeygenIntegration
        const heygenIntegration = data.integrations.find((integration) => integration.type === IntegrationType.HEYGEN)
        if (heygenIntegration && heygenIntegration.active) {
            return heygenIntegration
        }
        return defaultHeygenIntegration
    }, [data.integrations])

    const [
        isHeygenReadyToPlay,
        isHeygenLoadingSession,
        isHeygenLoadingRepeat,
        heygenSession,
        startHeygenSession,
        handleHeygenSpeak,
        handleHeygenInterruption,
        handleHeygenEndSession,
        isHeygenSpeaking,
    ] = useHeygenRealtime(
        getHeygenIntegration.token,
        videoRef,
        getHeygenIntegration.avatar_id,
        queryParams.get('lang') ?? 'en'
    )
    const [list, loading, sendMessage, onLoadMore, fetchHistory, appendToList, clearList] = usePeperoniAiChat(
        '',
        data.currentSession?.id || -1
    )

    console.debug({ isHeygenSpeaking, env: process.env })

    const initHeygenSession = async () => {
        if (isHeygenVisible) {
            if (!isHeygenLoadingSession) {
                const instance = await startHeygenSession()
                if (!instance) {
                    setIsHeygenVisible(false)
                }
            } else {
                void handleHeygenEndSession()
            }
        }
    }

    useEffect(() => {
        if (getHeygenIntegration.token !== '') {
            void initHeygenSession()
        }
        return () => {
            void handleHeygenEndSession()
        }
    }, [isHeygenVisible])

    useEffect(() => {
        void checkMicrophonePermissions()
    }, [])

    const checkMicrophonePermissions = async () => {
        await navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
                console.debug('microphone permission - true', stream)
            })
            .catch((err) => () => {
                console.debug('microphone permission - false', err)
            })
    }

    const handleStartListening = () => {
        resetTranscript()
        void handleHeygenInterruption()
        void SpeechRecognition.startListening({ continuous: true, language: 'it-IT' })
    }

    const handleStopListening = async () => {
        void SpeechRecognition.stopListening()
        if (finalTranscript) {
            void onSendMessage(finalTranscript)
        }
    }
    useEffect(() => {
        if (isHeygenSpeaking) {
            void handleHeygenInterruption()
        }
        if (finalTranscript) {
            void onSendMessage(finalTranscript)
            resetTranscript()
        }
    }, [finalTranscript])

    useEffect(() => {
        setHeaderMessage(data.currentInstance?.welcome_message ?? '')

        if (data.currentSession && data.currentSession.id !== -1) {
            void fetchHistory()
        }
    }, [data.currentSession, data.currentInstance])

    const loadHints = async () => {
        if (selectedTopic !== undefined) {
            const hints = await getHints(selectedTopic)
            setHints(hints)
        }
    }

    useEffect(() => {
        void loadHints()
    }, [selectedTopic])

    const groupedList = groupListByDate(list)

    const onSendMessage = useCallback(
        (speakedMessage?: string) => {
            const message = speakedMessage || messageText
            // eslint-disable-next-line no-async-promise-executor
            void new Promise<void>(async (resolve) => {
                if (!data.currentSession) return
                const response = await sendMessage(
                    {
                        message,
                        selectedParams: [],
                        context: false,
                    },
                    data.currentSession.id
                )
                resolve()
                if (heygenSession) {
                    await handleHeygenSpeak(response)
                }
            }).then(() =>
                setTimeout(() => {
                    listRef.current?.scrollToBottom()
                    setMessageText('')
                }, 100)
            )

            setTimeout(() => {
                listRef.current?.scrollToBottom()
                setMessageText('')
            }, 100)
        },
        [messageText, listRef, data.currentSession, listening, heygenSession, finalTranscript]
    )

    const realtimeVideo = useMemo(() => {
        if (!isHeygenVisible) return null

        return (
            <div className={`realtime-video-container ${isHeygenSpeaking ? 'speaking' : ''}`}>
                {isHeygenLoadingSession ? (
                    <div className="realtime-loader">
                        <CircularLoader />
                    </div>
                ) : (
                    <video ref={videoRef} className="realtime-video" muted controls={false} />
                )}
            </div>
        )
    }, [isHeygenVisible, isHeygenReadyToPlay, isHeygenLoadingSession, isHeygenSpeaking])

    if (data.currentInstance === null) {
        return null
    }
    return (
        <>
            {alertMessage && (
                <AlertBox
                    message={alertMessage.body}
                    onConfirm={() => {
                        setAlertMessage(undefined)
                        setHeaderMessage(data.currentInstance?.welcome_message ?? ' ')
                        setSelectedTopic(undefined)
                        setSelectedHint(undefined)
                        setHints([])
                        clearList()
                    }}
                    onCancel={() => setAlertMessage(undefined)}
                />
            )}
            {isHeygenVisible && (
                <div className="realtime-wrapper">
                    <div
                        className="realtime-background"
                        style={{
                            opacity: isHeygenSpeaking ? 1 : 0,
                            visibility: isHeygenSpeaking ? 'visible' : 'hidden',
                        }}
                    />

                    {realtimeVideo}
                </div>
            )}
            <div className="chat-panel-wrapper">
                <div className="chat-panel-background" />
                {list.length === 0 && data.currentInstance && headerMessage && (
                    <div className="welcome-chat-flow">
                        <div className="welcome-message-container">
                            <Typewriter
                                text={headerMessage}
                                delay={10}
                                onScrollTick={() => listRef.current?.scrollToBottom()}
                                onFinish={() => listRef.current?.scrollToBottom()}
                            />
                        </div>

                        {/* Type selected hint description */}
                        {selectedHint !== undefined ? (
                            <Typewriter
                                text={selectedHint.static_response ?? ''}
                                delay={10}
                                onScrollTick={() => listRef.current?.scrollToBottom()}
                                onFinish={() => listRef.current?.scrollToBottom()}
                            />
                        ) : null}

                        {/* List available hints for selected topic */}
                        {selectedHint === undefined && selectedTopic && hints.length > 0 ? (
                            <HintsSection
                                hints={hints}
                                topic={selectedTopic}
                                onSelect={async (hint) => {
                                    if (!!hint.prompt && hint.prompt.length > 0) {
                                        await sendMessage(
                                            {
                                                message: hint.prompt,
                                                selectedParams: [],
                                            },
                                            (data.currentSession && data.currentSession.id) ?? -1
                                        )
                                    } else {
                                        appendToList([
                                            {
                                                id: '',
                                                content: hint.title,
                                                contentType: ContentType.Text,
                                                datetime: new Date().getTime(),
                                                messageType: MessageType.Request,
                                            },
                                            {
                                                id: '',
                                                content: hint.static_response,
                                                contentType: ContentType.Text,
                                                datetime: new Date().getTime(),
                                                messageType: MessageType.Response,
                                            },
                                        ])
                                    }
                                }}
                                onBack={() => {
                                    setHeaderMessage(data.currentInstance?.welcome_message ?? ' ')
                                    setHints([])
                                    setSelectedTopic(undefined)
                                }}
                            />
                        ) : null}

                        {/* Show available topics */}
                        {selectedHint === undefined && selectedTopic === undefined && data.topics !== null ? (
                            <TopicSection
                                topics={data.topics}
                                onSelect={(item) => {
                                    if (item.description !== null && item.description.length > 1) {
                                        setHeaderMessage(item.description)
                                    }

                                    setSelectedTopic(item)
                                }}
                            />
                        ) : null}
                    </div>
                )}
                <ChatList
                    ref={listRef}
                    messageList={groupedList}
                    onSendError={(id) => {
                        const foundMessage = list.find((message) => message.id === `${parseInt(id, 10)}-request`)
                        if (!data.currentSession) return
                        void sendMessage(
                            {
                                message: (foundMessage as ParsedMessage).content,
                                selectedParams: [],
                                context: false,
                            },
                            data.currentSession.id
                        )
                    }}
                    loading={loading}
                    onLoadMore={() => void onLoadMore()}
                />
                <div className="chat-input-section">
                    <div className="chat-input-section__inner">
                        {list.length > 0 && (
                            <Tooltip position="top" text={t('general.restart')}>
                            <div
                                className="chat-input-section__inner__button chat-input-section__inner__button--primary"
                                style={{ marginRight: 8 }}
                            >
                                <FontAwesomeIcon
                                    icon={faRedo}
                                    onClick={() => {
                                        setAlertMessage({
                                            title: '',
                                            body: t('general.menu-alert-body'),
                                        })
                                    }}
                                />
                            </div>
                            </Tooltip>
                        )}
                        <ChatUserInput
                            value={messageText}
                            onChange={(newVal) => setMessageText(newVal)}
                            handleSendMessage={() => onSendMessage()}
                        />
                        {getHeygenIntegration.token !== '' && (
                            <Tooltip position="top" text={t('general.avatar')}>
                                <div
                                    className={`chat-input-section__inner__button chat-input-section__inner__button--primary ${
                                        isHeygenVisible ? 'active' : ''
                                    }`}
                                    onClick={() => setIsHeygenVisible(!isHeygenVisible)}
                                >
                                    <FontAwesomeIcon icon={faUser} />
                                </div>
                            </Tooltip>
                        )}
                        <Tooltip position="top" text={t('general.talk')}>
                            <div
                                className={`chat-input-section__inner__button chat-input-section__inner__button--primary ${
                                    listening ? 'active' : ''
                                }`}
                                onClick={() => (listening ? void handleStopListening() : void handleStartListening())}
                            >
                                <FontAwesomeIcon icon={faMicrophone} />
                            </div>
                        </Tooltip>
                        <Tooltip position="top" text={t('general.send')}>
                            <div
                                className={`chat-input-section__inner__button chat-input-section__inner__button--primary ${
                                    messageText ? '' : 'disabled-chat-input-button'
                                }`}
                                onClick={() => onSendMessage()}
                            >
                                <FontAwesomeIcon icon={faPaperPlane} />
                            </div>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ChatPanel
